import React, { useState, useRef, useEffect } from 'react';
import { User, getUserInfoWithCache, getUsers } from '../../api/user';
import { getCurrentUserID, getCurrentUserInfo } from '../../utils/userUtils';
import './UserSearch.css';

interface UserSearchProps {
  value: string | null;
  onChange: (userID: string | null, options?: {
    updateChildren?: boolean;
    updateAllSameAssignee?: boolean;
  }) => void;
  disabled?: boolean;
  visible: boolean;
  onClose: () => void;
}

// 移除本地 globalUserCache

const UserSearch: React.FC<UserSearchProps> = ({ value, onChange, disabled, visible, onClose }) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [updateChildren, setUpdateChildren] = useState(false);
  const [updateAllSameAssignee, setUpdateAllSameAssignee] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchDialogRef = useRef<HTMLDivElement>(null);

  // 获取当前用户信息，只请求一次，后续都用缓存
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userID = getCurrentUserID();
        const user = await getUserInfoWithCache(userID);
        if (user) {
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('获取当前用户信息失败:', error);
      }
    };
    fetchCurrentUser();
  }, []);


  // 获取用户昵称首字（与 UserInfo 组件逻辑一致）
  const getUserInitial = (user: User | { userNickName?: string; userName?: string } | null) => {
    if (!user) return 'U';
    const name = (user as any).userNickName || (user as any).userName || '';
    if (!name) return 'U';
    // 如果是中文，取第一个字符
    if (/[\u4e00-\u9fa5]/.test(name)) {
      return name.charAt(0);
    }
    // 如果是英文，取第一个字符的大写
    return name.charAt(0).toUpperCase();
  };

  // 只保留输入状态，候选用户由服务端按需搜索。
  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  useEffect(() => {
    const keyword = searchText.trim();
    if (!visible || !keyword) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      let userOrgID: string | undefined;
      try {
        userOrgID = getCurrentUserInfo().userOrgID;
      } catch {
        // 无组织信息时仍由后端权限边界决定可见用户。
      }
      void getUsers({ keyword, userOrgID, limit: 20 })
        .then(response => {
          if (!cancelled) setSearchResults((response.data || []).filter(user => user.userID !== getCurrentUserID()));
        })
        .catch(error => {
          if (!cancelled) {
            setSearchResults([]);
            console.warn('搜索用户失败:', error);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchText, visible]);

  // 处理点击外部关闭搜索框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  const handleSelect = (user: User) => {
    onChange(user.userID, {
      updateChildren,
      updateAllSameAssignee
    });
    onClose();
    setSearchText('');
  };

  if (!visible) return null;

  return (
    <div className="user-search" ref={searchRef}>
      <div className="user-search-dialog" ref={searchDialogRef}>
        <div className="user-search-input">
          <input
            type="text"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="搜索昵称、真实姓名..."
            autoFocus
          />
        </div>
        <div className="user-search-results">
          {!searchText ? (
            // 无搜索文本时显示"我"和批量更新选项
            <>
              {currentUser && (
                <div 
                  className="user-search-result-item current-user"
                  onClick={() => handleSelect(currentUser)}
                >
                  <div className="budgetItemAssignee-user-avatar-initial">
                    {getUserInitial(currentUser)}
                  </div>
                  <span className="budgetItemAssignee-user-name">我（{currentUser.userNickName || currentUser.userName}）</span>
                </div>
              )}
              <div className="user-search-options">
                <label className="user-search-option">
                  <input
                    type="checkbox"
                    checked={updateChildren}
                    onChange={(e) => setUpdateChildren(e.target.checked)}
                  />
                  <span>同时更换所有子预算</span>
                </label>
                <label className="user-search-option">
                  <input
                    type="checkbox"
                    checked={updateAllSameAssignee}
                    onChange={(e) => setUpdateAllSameAssignee(e.target.checked)}
                  />
                  <span>同时更换所有该原负责人</span>
                </label>
              </div>
            </>
          ) : (
            <>
              {/* "我"选项始终显示 */}
              {currentUser && (
                <div 
                  className="user-search-result-item current-user"
                  onClick={() => handleSelect(currentUser)}
                >
                  <div className="budgetItemAssignee-user-avatar-initial">
                    {getUserInitial(currentUser)}
                  </div>
                  <span className="budgetItemAssignee-user-name">{currentUser.userNickName || currentUser.userName}</span>
                </div>
              )}
              
              {/* 搜索结果 */}
              {searchResults.length > 0 ? (
                searchResults.map(user => (
                  <div 
                    key={user.userID}
                    className="user-search-result-item"
                    onClick={() => handleSelect(user)}
                  >
                    <div className="budgetItemAssignee-user-avatar-initial">
                      {getUserInitial(user)}
                    </div>
                    <span className="budgetItemAssignee-user-name">{user.userNickName || user.userName}</span>
                  </div>
                ))
              ) : (
                <div className="user-search-empty">未找到其他用户</div>
              )}

              {/* 批量更新选项始终显示 */}
              <div className="user-search-options">
                <label className="user-search-option">
                  <input
                    type="checkbox"
                    checked={updateChildren}
                    onChange={(e) => setUpdateChildren(e.target.checked)}
                  />
                  <span>同时更换所有子预算</span>
                </label>
                <label className="user-search-option">
                  <input
                    type="checkbox"
                    checked={updateAllSameAssignee}
                    onChange={(e) => setUpdateAllSameAssignee(e.target.checked)}
                  />
                  <span>更换所有该负责人</span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSearch;
